import { useState } from 'react';
import { send } from '@/shared/messages';
import { Button } from '@/components/Button';
import { Alert } from '@/components/Alert';

export function GenerateActions({
  chapterId,
  hasSummary,
  cardCount,
  hasContent,
  onSummaryUpdated,
  onCardsUpdated,
}: {
  chapterId: string;
  hasSummary: boolean;
  cardCount: number;
  hasContent: boolean;
  onSummaryUpdated: () => void;
  onCardsUpdated: () => void;
}) {
  const [busy, setBusy] = useState<'summary' | 'cards' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function genSummary() {
    setBusy('summary');
    setError(null);
    try {
      await send({ type: 'GENERATE_SUMMARY', chapterId });
      onSummaryUpdated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function genCards(replace: boolean) {
    setBusy('cards');
    setError(null);
    try {
      await send({ type: 'GENERATE_CARDS', chapterId, replace });
      onCardsUpdated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const disabled = !hasContent;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={genSummary}
          disabled={disabled || busy !== null}
          loading={busy === 'summary'}
          title={hasSummary ? 'Replaces the existing summary.' : 'Generate TL;DR + key sections.'}
        >
          {busy === 'summary'
            ? 'Generating summary…'
            : hasSummary
              ? 'Regenerate summary'
              : 'Generate summary'}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => genCards(false)}
          disabled={disabled || busy !== null}
          loading={busy === 'cards'}
          title={
            cardCount > 0
              ? 'Adds more cards alongside existing ones.'
              : 'Generate flashcards from all attached sources.'
          }
        >
          {busy === 'cards'
            ? 'Generating cards…'
            : cardCount > 0
              ? `Generate more cards (have ${cardCount})`
              : 'Generate cards'}
        </Button>
        {cardCount > 0 && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              if (
                confirm(
                  `Replace all ${cardCount} existing cards for this chapter? Review history is preserved separately.`
                )
              ) {
                void genCards(true);
              }
            }}
            disabled={busy !== null}
          >
            Replace cards
          </Button>
        )}
      </div>
      {!hasContent && (
        <p className="text-xs text-slate-500">
          Attach a chapter text or video transcript before generating.
        </p>
      )}
      {error && <Alert>{error}</Alert>}
    </div>
  );
}
